<template>
  <div>
    <TransitionRoot as="template" :show="!!props.open">
      <Dialog as="div" class="relative z-backdrop-reset" @close="close(false)">
        <Backdrop @click="close(false)" />

        <div class="z-backdrop-content fixed inset-0 overflow-hidden">
          <div class="absolute inset-0 overflow-hidden">
            <div
              class="pointer-events-none fixed inset-y-0 right-0 flex max-w-full sm:pl-10"
            >
              <TransitionChild
                as="template"
                enter="transform transition ease-in-out duration-200"
                enter-from="translate-x-full"
                enter-to="translate-x-0"
                leave="transform transition ease-in-out duration-300"
                leave-from="translate-x-0"
                leave-to="translate-x-full"
              >
                <DialogPanel class="pointer-events-auto w-screen max-w-[500px]">
                  <div
                    class="flex h-full flex-col overflow-y-auto bg-white shadow-xl"
                  >
                    <!-- header -->
                    <div
                      class="px-5 pb-4 pt-6 flex flex-row items-center justify-between"
                    >
                      <div class="flex flex-row items-center gap-2">
                        <p
                          v-if="!details"
                          class="text-xl font-semibold text-gray-900"
                        >
                          {{ createLabel ?? defaultLabels.create }}
                        </p>
                        <p v-else class="text-xl font-semibold text-gray-900">
                          {{ title ?? t("admin.settings.details") }}
                        </p>
                        <Tooltip
                          v-if="!canUpdate"
                          :content="t('admin.settings.read_only')"
                        >
                          <Eye class="w-5 text-gray-600" />
                        </Tooltip>
                      </div>
                      <DropdownButton
                        :options="[
                          {
                            key: 'delete',
                            label: deleteLabel ?? defaultLabels.delete,
                            icon: TrashOne,
                            onClick: () => (showDeleteConfirmation = true),
                            show: () => details?.id && canDelete,
                          },
                          ...options,
                        ]"
                        :eventData="details"
                      />
                    </div>

                    <!-- Fields -->
                    <div
                      class="flex flex-col flex-1 overflow-auto pb-12"
                      :class="
                        tabs?.length
                          ? ''
                          : 'pt-6 border border-gray-200 px-4 sm:px-6'
                      "
                    >
                      <Errors />
                      <PanelTabs
                        v-if="!fields?.length && tabs?.length > 0"
                        :tabs="tabs"
                      >
                        <template #tab-fields="{ tab }">
                          <template v-for="(fieldOrGroup, index) in tab.fields">
                            <div
                              v-if="Array.isArray(fieldOrGroup)"
                              :key="'settingsPanelGroup_' + index"
                              class="flex flex-col gap-2 sm:gap-4 pb-1 mb-1 sm:pb-2 sm:mb-2 border-b last:border-none border-gray-300 px-4 pb-2"
                            >
                              <DynamicInputComponent
                                v-for="field in fieldOrGroup"
                                :key="field.key"
                                :modelValue="form[field.key]"
                                @update:modelValue="onInput"
                                :field="field"
                                :form="form"
                                :disabled="checkFieldDisabled(field)"
                                compact
                                class="!mb-0 !pb-0"
                                :debounce="false"
                                :limitWidth="false"
                              />
                            </div>
                            <DynamicInputComponent
                              v-else
                              :key="fieldOrGroup.key"
                              :modelValue="form[fieldOrGroup.key]"
                              @update:modelValue="onInput"
                              :field="fieldOrGroup"
                              :form="form"
                              :disabled="checkFieldDisabled(fieldOrGroup)"
                              compact
                              class="border-b last:border-none border-gray-300 px-4 pb-2"
                              :debounce="false"
                              :limitWidth="false"
                            />
                          </template>
                        </template>
                      </PanelTabs>

                      <div v-if="fields?.length">
                        <!-- Repeat inputs here based on prop -->
                        <DynamicInputComponent
                          v-for="field in fields"
                          :key="field.key"
                          :modelValue="form[field.key]"
                          @update:modelValue="onInput"
                          :field="field"
                          :form="form"
                          :disabled="checkFieldDisabled(field)"
                          compact
                          :debounce="false"
                          class="pb-1 mb-1 sm:pb-2 sm:mb-2"
                          :limitWidth="false"
                        />
                      </div>

                      <slot name="after" />
                    </div>

                    <!-- Footer -->
                    <div
                      class="p-5 pt-4 w-full flex flex-row items-center justify-between shadow-[0px_4px_60px_0px_rgba(0,0,0,0.25)]"
                    >
                      <button
                        @click="close(false)"
                        type="button"
                        class="rounded-md border border-gray-300 text-gray-700 bg-white px-2.5 py-2 text-base font-medium shadow-sm hover:text-gray-500 focus:outline-none sm:w-auto sm:text-sm disabled:opacity-50"
                      >
                        {{ t("components.buttons.cancel") }}
                      </button>
                      <primary-button
                        v-if="
                          (details?.id && canUpdate) ||
                          (!details?.id && canCreate)
                        "
                        :label="t('components.buttons.save_changes')"
                        :disabled="
                          !!props.isCreateLoading || !!props.isUpdateLoading
                        "
                        @click="handleSaveChanges"
                      />
                    </div>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>

    <ConfirmationModal
      :open="showDeleteConfirmation"
      @close="showDeleteConfirmation = false"
      @confirm="handleDelete"
      :title="deleteConfirmation?.title"
      :confirmationLabel="deleteConfirmation?.confirm"
      :cancelLabel="deleteConfirmation?.cancel"
      :disabled="props.isDeleteLoading"
    >
      <template v-if="confirmationMessage">
        <p
          v-for="(message, index) in confirmationMessage"
          :key="index"
          class="m-0"
        >
          {{ message }}
        </p>
      </template>
    </ConfirmationModal>
  </div>
</template>

<script setup>
import { useI18n } from "vue-i18n";
import { useForm } from "@inertiajs/vue3";
import { computed, onMounted, ref, watch } from "vue";

import {
  Dialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot,
} from "@headlessui/vue";

import TrashOne from "@/Tailwind/Icon/TrashOne.vue";
import Eye from "@/Tailwind/Icon/Eye.vue";
import Tooltip from "@/Tailwind/Components/Tooltip.vue";

import PrimaryButton from "@/Tailwind/Components/Buttons/PrimaryButton.vue";
import ConfirmationModal from "@/Tailwind/Components/ConfirmationModal.vue";
import DynamicInputComponent from "@/Tailwind/Components/Settings/DynamicInputComponent.vue";
import Errors from "@/Tailwind/Components/Errors.vue";
import Backdrop from "@/Tailwind/Components/Backdrop.vue";
import PanelTabs from "@/Tailwind/Components/Settings/PanelTabs.vue";
import DropdownButton from "@/Tailwind/Components/DropdownButton";
import { useFormSetup } from "@/Tailwind/Mixins/useFormSetup";

const { t } = useI18n();

const showDeleteConfirmation = ref(false);

const emit = defineEmits(["close", "create", "update", "delete"]);
const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
  fields: {
    type: Array,
    required: false,
  },
  tabs: {
    type: Array,
    required: false,
  },
  details: Object,
  createLabel: {
    type: String,
  },
  updateLabel: {
    type: String,
  },
  deleteLabel: {
    type: String,
  },
  canCreate: {
    type: Boolean,
    default: false,
  },
  canUpdate: {
    type: Boolean,
    default: false,
  },
  canDelete: {
    type: Boolean,
    default: false,
  },
  // An object of strings to use in place of the default labels
  // Possible keys are: title, confirm, cancel, message
  deleteConfirmation: {
    type: Object,
  },
  icon: {
    type: Object,
  },
  title: {
    type: String,
  },
  options: {
    type: Array,
    default: () => [],
  },
  isCreateLoading: {
    type: Boolean,
    default: false,
  },
  isUpdateLoading: {
    type: Boolean,
    default: false,
  },
  isDeleteLoading: {
    type: Boolean,
    default: false,
  },
});

const { mapDetailsToForm } = useFormSetup();

// We'll override this so the keys get set
// When the keys are not set initially they are not reactive
let form = useForm({});

const defaultLabels = {
  create: t("admin.settings.add"),
  update: t("admin.settings.update"),
  delete: t("admin.settings.delete"),
};

const close = (reload = false) => emit("close", reload);

const handleDelete = () => {
  emit("delete");
  showDeleteConfirmation.value = false;
};

const handleSaveChanges = () => {
  if (!props.canUpdate && !props.canCreate) {
    return;
  }

  if (props.details?.id) {
    emit("update", form);
    return;
  }

  emit("create", form);
};

watch(
  () => props.open,
  () => {
    if (!props.open) {
      return;
    }

    form = mapDetailsToForm(props.fields, props.details, props.tabs);
  }
);

onMounted(() => {
  // If the settings panel is mounted in open state, it will not trigger the watcher
  if (props.open) {
    form = mapDetailsToForm(props.fields, props.details, props.tabs);
  }
});

const checkFieldDisabled = (field) => {
  // If details are set, check if we can update
  if (!props.canUpdate && props.details?.id) {
    return true;
  }
  // If details are not set, check if we can create
  else if (!props.canCreate && !props.details?.id) {
    return true;
  }

  if (typeof field.disabled === "function" && field.disabled(form)) {
    return true;
  }

  if (typeof field.disabled === "boolean" && field.disabled) {
    return true;
  }

  return false;
};

// We can't use vModel because the dynamic input component will
// emit different fields if it is a composite field
const onInput = (field, value) => {
  form[field.key] = value;
};

const confirmationMessage = computed(() => {
  if (props.deleteConfirmation?.message) {
    return props.deleteConfirmation.message;
  }

  return [t("admin.settings.confirmation.message")];
});
</script>
